import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { CropperSettings } from 'ngx-img-cropper';
import { config } from '../../config/config';
import { HubEvent } from '../models/hub/hub.event';
import { HubPlace } from '../models/hub/hub.place';
import { VisitorDataCache } from '../stores/visitor.calendar.store';
import { ConfigService } from './config.service';
import { ImgDataObject } from './export.csv.service';

@Injectable()
export class ImageService {
  // need to check why this config is not merged on production server
  public prodPrefix = config.serverDomain; //'https://imgoingcalendar.com/';

  constructor(private domSanitizer: DomSanitizer) {
    //ConfigService.config.assetImageServeUrl = 'https://imgoingcalendar.com/';
  }

  public safeImage(url: string): SafeUrl {
    return this.domSanitizer.bypassSecurityTrustUrl(url);
  }

  public safeUrl(url: string): SafeResourceUrl {
    return this.domSanitizer.bypassSecurityTrustResourceUrl(url);
  }

  public imageSource(item: HubEvent | HubPlace)  {
    if (!item) {
      return VisitorDataCache.logo || this.prodPrefix + config.defaultImageUrl;
    }
    let source = item.cover && item.cover.source;

    if (!source) {
      return VisitorDataCache.logo || this.prodPrefix + config.defaultImageUrl;
    }
    if (item.cover.source.startsWith('assets/imgs/')) {
      //if (item.cover.isDefault) {
      return this.prodPrefix + item.cover.source;
    }

    if (item.cover.source.startsWith('data:')) {
      return item.cover.source;
    }

    // if (item.isManuallyCreated || item.isFacebookEvent || item.isPromotionImage) {
    if (!item.cover.source.startsWith('http')) {
      return ConfigService.config.imageServeUrl + item.cover.source;
      //return 'https://api.imgoingcalendar.com/imgs/' + item.cover.source;
    }

    return item.cover.source;
  }

  public imageSourceUrl(imageUrl: string, useLogoIfNoImage = true) {
    if (!imageUrl) {
      if (useLogoIfNoImage) {
        return VisitorDataCache.logo || this.prodPrefix + config.defaultImageUrl;
      }
      return this.prodPrefix + config.defaultImageUrl;
    }

    if (imageUrl.indexOf(config.defaultImageDirectory) >= 0 && !imageUrl.startsWith('http')) {
      return this.prodPrefix + imageUrl;
    }

    if (imageUrl.startsWith('data:')) {
      return imageUrl;
    }

    if (imageUrl.startsWith('assets/imgs/')) {
      return this.prodPrefix + imageUrl;
    }

    if (!imageUrl.startsWith('http')) {
      return ConfigService.config.imageServeUrl + imageUrl;
      //return 'https://api.imgoingcalendar.com/imgs/' + imageUrl;
    }

    return imageUrl;
  }

  public isImUploadedImage(img) {
    //return img.indexOf(ConfigService.config.imageServeUrl) !== -1;
    return (
      img.indexOf('localhost:3000') !== -1 ||
      img.indexOf('imgoing-prod-api.xyz') !== -1 ||
      img.indexOf('api.imgoingcalendar.com') !== -1 ||
      img.indexOf('imgoing-dev-api.tk') !== -1 ||
      img.startsWith('data:')
    );
  }

  public getBase64ByImageFile(imageFile: File): Promise<any> {
    return new Promise((resolve, reject) => {
      if (imageFile) {
        try {
          let image = new Image();
          let FR = new FileReader();
          let canvas = document.createElement('canvas');
          let context = canvas.getContext('2d');

          FR.addEventListener('load', function (e) {
            image.src = (e as any).target.result;
            //resolve((e as any).target.result);
          });
          FR.readAsDataURL(imageFile);
          image.onload = () => {
            let targetSize = this.compressImage(image);

            canvas.width = targetSize.targetWidth;
            canvas.height = targetSize.targetHeight;
            context.drawImage(image, 0, 0, targetSize.targetWidth, targetSize.targetHeight);
            const data = canvas.toDataURL(imageFile.type || 'image/png');
            resolve(data);
          };
        } catch (ex) {
          reject(ex);
        }
      } else {
        resolve(null);
      }
    });
  }

  public geetBase64ImageByUrl(imgUrl): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!imgUrl) {
        resolve(null);
      }
      if (this.isBase64Image(imgUrl)) {
        resolve(imgUrl);
      } else {
        let img = document.createElement('img');
        img.crossOrigin = 'anonymous';
        img.onload = (x) => {
          let base64 = null;
          try {
            let image = x.srcElement as HTMLImageElement;
            base64 = this.getBase64Image(image);
            resolve(base64);
          } catch (e) {
            reject(e);
          }
        };
        img.src = this.imageSourceUrl(imgUrl);
      }
    });
  }

  private getBase64Image(img: HTMLImageElement, imageType = 'image/png') {
    let canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    let ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    let dataURL = canvas.toDataURL(imageType);
    return dataURL;
  }

  public isBase64Image(url) {
    return url.startsWith('data:');
  }

  public getImageFileByBase64(base64) {
    let blob = this.dataURLtoBlob(base64);
    let file = this.blobToFile(blob, 'fileName.jpg');
    return file;
  }

  public createImageCropperSetting(width, height, isKeepAspect, canvasHeight = 250): CropperSettings {
    let setting = new CropperSettings();
    setting.width = width;
    setting.height = height;
    setting.croppedWidth = width;
    setting.croppedHeight = height;
    setting.canvasWidth = width;
    setting.canvasHeight = canvasHeight;
    setting.preserveSize = true;
    setting.compressRatio = 1;
    setting.keepAspect = isKeepAspect;
    return setting;
  }

  public async compressImageFile(file: File, width = 1200): Promise<File> {
    const base64 = await this.compressImageFileToBase64(file, width);
    let ext = 'jpg';
    if (file.name.indexOf('.') >= 0 && file.name.indexOf('.jpg') < 0) {
      ext = file.name.split('.')[1];
    }
    let imgFile = this.blobToFile(this.dataURLtoBlob(base64), `image.${ext}`);
    return imgFile;
  }

  public compressImageFileToBase64(file: File, width = 1200): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(null);
      }
      let reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        let image = new Image();
        image.src = (e.target as any).result;
        image.onload = () => {
          let canvas = document.createElement('canvas'),
            context = canvas.getContext('2d'),
            data = '';

          let targetSize = this.compressImage(image, width);
          canvas.width = targetSize.targetWidth;
          canvas.height = targetSize.targetHeight;

          console.log('targetSize', targetSize);

          context.drawImage(image, 0, 0, targetSize.targetWidth, targetSize.targetHeight);
          let type = 'image/jpeg',
            ext = 'jpg';
          if (file.name.indexOf('.') >= 0 && file.name.indexOf('.jpg') < 0) {
            ext = file.name.split('.')[1];
            type = `image/${ext}`;
          }
          data = canvas.toDataURL(type);
          resolve(data);
        };
      };
    });
  }

  private compressImage(image, width = 1200) {
    let originWidth = image.naturalWidth,
      originHeight = image.naturalHeight;

    //let maxWidth = 960, maxHeight = 960;
    let maxWidth = width,
      maxHeight = width;
    let targetWidth = originWidth,
      targetHeight = originHeight;
    if (originWidth > maxWidth || originHeight > maxHeight) {
      if (originWidth / originHeight > maxWidth / maxHeight) {
        targetWidth = maxWidth;
        targetHeight = Math.round(maxWidth * (originHeight / originWidth));
      } else {
        targetHeight = maxHeight;
        targetWidth = Math.round(maxHeight * (originWidth / originHeight));
      }
    }
    return {
      targetWidth,
      targetHeight,
    };
  }

  private dataURLtoBlob(imgBase64) {
    let arr = imgBase64.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], {type: mime});
  }

  private blobToFile(theBlob, fileName): File {
    theBlob.lastModifiedDate = new Date();
    theBlob.lastModified = new Date();
    theBlob.name = fileName;
    return theBlob;
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/vsts_build/_work/2/s/web/src/services/image.service.ts